import { useDataTable } from '@/components/data-table/data-table-provider'
import { FilterToggleButton } from '@/components/FilterToggleButton'
import { useHotKey } from '@/hooks/use-hot-key'
import { useControls } from '@/components/controls'
import { cn } from '@/lib/utils'
import { DataTableFilterControlsDrawer } from './data-table-filter-controls-drawer'
import { DataTableViewOptions } from './data-table-view-options'

interface DataTableToolbarProps {
  renderActions?: () => React.ReactNode
  /** A row under the toolbar's buttons, e.g. the applied filters. */
  filters?: React.ReactNode
}

export function DataTableToolbar({
  renderActions,
  filters,
}: DataTableToolbarProps) {
  const { table, filterFields } = useDataTable()
  // Tables with no filter fields (e.g. the v3 data tables) shouldn't show a
  // Filters toggle — it would open an empty rail. Hide the toggle + mobile drawer.
  const hasFilters = filterFields.length > 0
  const { open, setOpen } = useControls()
  // No-op the toggle shortcut when the table has no filters (nothing to open).
  useHotKey(() => {
    if (hasFilters) setOpen((prev) => !prev)
  }, 'b')
  useHotKey(table.resetColumnFilters, 'Escape')

  return (
    <div className="flex flex-wrap items-center gap-3">
      {/* Left toolbar column — the Filters toggle, above the filter rail. When
          expanded it matches the rail width so the Hide Filters toggle lands
          directly above the first rail field. Collapses to auto width when the
          rail is hidden so it slides flush-left. Hidden entirely when the table
          has no filter fields (e.g. the v3 data tables). */}
      {hasFilters ? (
        <div
          className={cn(
            'hidden shrink-0 items-center justify-start gap-1 px-3 transition-[width] sm:flex sm:w-auto',
            'sm:group-data-[expanded=true]/controls:w-52 md:group-data-[expanded=true]/controls:w-72'
          )}>
          <FilterToggleButton
            open={open}
            onToggle={() => setOpen((prev) => !prev)}
          />
        </div>
      ) : null}
      {filters ? (
        <div className="order-last flex min-w-0 basis-full flex-wrap items-center gap-2 px-3">
          {filters}
        </div>
      ) : null}
      <div className="flex items-center gap-1 pl-3 sm:hidden">
        {hasFilters ? <DataTableFilterControlsDrawer /> : null}
        <DataTableViewOptions />
      </div>
      {/* Right toolbar column — View options + actions (e.g. "Updated … ago"),
          right-aligned via `ml-auto`. Wraps below the left column on very
          narrow headers (e.g. when the chat sidebar is open). */}
      <div className="ml-auto flex shrink-0 items-center gap-2 pr-3">
        <div className="hidden sm:flex">
          <DataTableViewOptions />
        </div>
        {renderActions?.()}
      </div>
    </div>
  )
}
